import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { darkTheme, FONT_MONO } from '../../theme/tokens';
import { backTarget, type ConnectionStatus } from './connectionModel';

const t = darkTheme;

// Small presentational pieces shared by the Connections list and detail views
// (SC-3122).

/** Read-only status. Each state carries its own word — never color alone. */
export function StatusBadge({ tone }: { tone: ConnectionStatus }) {
  const s = {
    on: { label: 'On', fg: t.accentText, bg: t.accentBg, border: 'transparent', dash: false },
    paused: { label: 'Paused', fg: t.text2, bg: 'transparent', border: t.border2, dash: true },
    removing: { label: 'Being removed', fg: t.redText, bg: t.redBg, border: 'transparent', dash: false },
    unavailable: { label: 'Unavailable', fg: t.amberText, bg: t.amberBgSubtle, border: t.amber, dash: true },
  }[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 600,
        padding: '2px 9px',
        borderRadius: 999,
        color: s.fg,
        background: s.bg,
        border: `1px ${s.dash ? 'dashed' : 'solid'} ${s.border}`,
      }}
    >
      {s.label}
    </span>
  );
}

export function Notice({ children, tone = 'plain' }: { children: ReactNode; tone?: 'plain' | 'error' }) {
  return (
    <div
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        padding: '14px 16px',
        border: `1px dashed ${tone === 'error' ? t.redBorder : t.border}`,
        borderRadius: 8,
        color: tone === 'error' ? t.redText : t.text3,
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

/** "← Connections", back to wherever the table was (filter included). */
export function BackToConnections() {
  const { state } = useLocation();
  return (
    <Link
      to={backTarget(state)}
      style={{ display: 'inline-block', fontSize: 13, color: t.text3, textDecoration: 'none', marginBottom: 16 }}
    >
      ← Connections
    </Link>
  );
}

export function DetailHeader({
  name,
  subtitle,
  status,
  action,
}: {
  name: string;
  subtitle: string;
  status: ConnectionStatus;
  /** Top-right control (the kill switch), when the caller may act. */
  action?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: t.text, margin: 0 }}>{name}</h1>
          <StatusBadge tone={status} />
        </div>
        <p style={{ fontSize: 13, color: t.text3, margin: '4px 0 0' }}>{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

/** The settings list: label → value (+ an optional explanation line). */
export function SettingsList({ children }: { children: ReactNode }) {
  return (
    <dl
      style={{
        margin: 0,
        border: `1px solid ${t.border}`,
        borderRadius: 8,
        background: t.surface,
      }}
    >
      {children}
    </dl>
  );
}

export function Setting({
  label,
  children,
  hint,
  tone = 'plain',
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  tone?: 'plain' | 'warn';
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        gap: 16,
        padding: '12px 16px',
        borderTop: `1px solid ${t.border}`,
        marginTop: -1,
      }}
    >
      <dt style={{ fontSize: 13, color: t.text3 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 13, color: tone === 'warn' ? t.amberText : t.text }}>
        {children}
        {hint && <div style={{ fontSize: 12, color: t.text3, marginTop: 2 }}>{hint}</div>}
      </dd>
    </div>
  );
}

/** An env-var NAME as a setting value. Never a value — nothing here can render a
 *  secret, because the API never sends one. */
export function CredentialSetting({ name }: { name: string | null }) {
  return (
    <Setting
      label="Credential"
      hint={name ? 'Read from this environment variable on EKOM’s servers.' : undefined}
    >
      {name ? <code style={{ fontFamily: FONT_MONO, fontSize: 12 }}>{name}</code> : 'None'}
    </Setting>
  );
}
